import {
  Box,
  Column,
  FormField,
  InputSelectField,
  Radio,
  RadioGroup,
  Row,
  Typography,
} from '@tempmee/react';
import { CollapsibleCard } from '../../../../commonComponents';
import { UserProfession } from '../../../../enums/UserProfession';
import { timePerPatientOptions } from '../../../../utils/Enum';
import {
  bookedOutPatientsOptions,
  numberOfOperatoriesOptions,
  numberOfPermanentStaffOptions,
} from '../../../registration/utils';
import { useFormContext } from '../../hooks/useFormContext';
import { CardTitle } from '../CardTitle';
import { Divider } from '../Divider';
import { PartnershipField } from './components/PartnershipField';
import { useOfficeInformation } from './hooks/useOfficeInformation';

const toSelectOptions = (items) =>
  items?.map((item) => ({ label: item, value: item })) ?? [];

export const OfficeInformationCard = ({ isOpen, onToggle }) => {
  const { setValue, control } = useFormContext();
  const { formValues, isValid, isProductivityOperatorsValid, hasWarning } =
    useOfficeInformation(control);

  return (
    <CollapsibleCard
      TitleComponent={() => (
        <CardTitle
          name="Office Information"
          showWarningIcon={!isOpen && (!isValid || hasWarning)}
        />
      )}
      isOpen={isOpen}
      onToggle={onToggle}
    >
      <Column gap={6} px={20} style={{ marginBottom: 40 }}>
        {/* Time per patient */}
        <FormField label='Select the expected "time per patient" for each profession' required>
          <Box style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 26 }}>
            {[
              { label: 'Dental Hygienist', field: 'timePerPatientRDH' },
              { label: 'Dental Assistant', field: 'timePerPatientDA' },
              { label: 'Associate Dentist', field: 'timePerPatientDN' },
            ].map(({ label, field }) => (
              <Column key={field} gap={1}>
                <Typography style={{ marginBottom: 10 }}>{label}:</Typography>
                <InputSelectField
                  value={formValues[field]}
                  onChange={(value) => setValue(field, value)}
                  options={toSelectOptions(timePerPatientOptions)}
                />
              </Column>
            ))}
          </Box>
        </FormField>

        {/* Assisted hygiene */}
        <FormField label="Is assisted hygiene available at your office for temp hygienists?" required>
          <RadioGroup
            value={
              formValues.isAssistedHygiene === true
                ? 'yes'
                : formValues.isAssistedHygiene === false
                  ? 'no'
                  : ''
            }
            onChange={(val) => setValue('isAssistedHygiene', val === 'yes')}
            orientation="horizontal"
          >
            <Row gap={8} style={{ marginLeft: 40 }}>
              <Radio value="yes" label="Yes" />
              <Radio value="no" label="No" />
            </Row>
          </RadioGroup>
        </FormField>

        <Divider />

        {/* Number of operatories */}
        <FormField label="Number of operatories" required>
          <InputSelectField
            value={formValues.productivity.operatories}
            onChange={(value) => setValue('productivity.operatories', value)}
            options={toSelectOptions(numberOfOperatoriesOptions)}
            isError={!isProductivityOperatorsValid || hasWarning}
          />
        </FormField>

        {/* Permanent staff */}
        <FormField label="Number of permanent staff at this office:" optional>
          <Box style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 26 }}>
            {[
              { title: 'Dental Hygienists', value: UserProfession.RDH },
              { title: 'Dental Assistants', value: UserProfession.DA },
              { title: 'Doctors', value: UserProfession.DN },
            ].map((item) => (
              <Column key={item.value} gap={1}>
                <Typography style={{ marginBottom: 8 }}>{item.title}:</Typography>
                <InputSelectField
                  value={formValues.productivity.permanentStaff[item.value]}
                  onChange={(value) => setValue(`productivity.permanentStaff.${item.value}`, value)}
                  options={toSelectOptions(numberOfPermanentStaffOptions)}
                />
              </Column>
            ))}
          </Box>
        </FormField>

        {/* Booked out patients */}
        <FormField label="How far are you currently booking out patients?" optional>
          <InputSelectField
            value={formValues.productivity.bookedOutPatients}
            onChange={(value) => setValue('productivity.bookedOutPatients', value)}
            options={toSelectOptions(bookedOutPatientsOptions)}
          />
        </FormField>

        <Divider />

        <PartnershipField />
      </Column>
    </CollapsibleCard>
  );
};
